import {
  AttentionModal,
  Button,
  ButtonType,
  SkeletonLoader,
  useModal,
  colors,
  fontWeights,
  sizing,
} from '@chillibean/component-library';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { DisplayNameType, useDisplayName } from '../../hooks';
import { SpecialLock } from '../presentational';
import { ReasonForLockModal } from './ReasonForLockModal';

export function ProtocolHeaderLoading() {
  return (
    <Wrapper>
      <HeaderWrapper>
        <SkeletonLoader height={34} width={160} />
        <SkeletonLoader height={34} width={160} />
      </HeaderWrapper>
      <ButtonWrapper>
        <SkeletonLoader height={40} width={90} />
      </ButtonWrapper>
    </Wrapper>
  );
}

export function ProtocolHeader(props) {
  const { header = undefined, loading = false, subHeader = undefined, withButton = true } = props;

  // HOOKS
  // Store
  const { data: studyProtocol } = useSelector((state) => state.protocolReducer.protocol);
  // Custom
  const {
    closeModal: closeReasonForLockModal,
    modalOpen: reasonForLockModalOpen,
    openModal: openReasonForLockModal,
  } = useModal();
  const { getDisplayName } = useDisplayName();
  // State
  const [attentionModal, setAttentionModal] = useState(false);

  const handleUnlock = () => {
    if (studyProtocol?.studyEventsPersisted) return;

    openReasonForLockModal();
  };

  const handleLock = () => {
    if (studyProtocol?.studyEventsPersisted) {
      setAttentionModal(true);
    } else {
      openReasonForLockModal();
    }
  };

  const closeModals = () => {
    closeReasonForLockModal();
    setAttentionModal(false);
  };

  const buttonArea = () =>
    studyProtocol?.locked ? (
      <IconContainer onClick={handleUnlock}>
        <SpecialLock
          disabled={studyProtocol?.studyEventsPersisted}
          tooltip={`Once there are persisted ${getDisplayName(
            DisplayNameType.STUDY_EVENT,
          ).humanized.plural.toLowerCase()} in any of the assigned sites, it is not possible to unlock a trial configuration.`}
        />
      </IconContainer>
    ) : (
      <ButtonWrapper>
        <Button onClick={handleLock} text="Lock" type={ButtonType.SECONDARY} />
      </ButtonWrapper>
    );

  if (loading) return <ProtocolHeaderLoading />;

  return (
    <>
      <AttentionModal
        confirmButtonText="Continue"
        onClose={() => setAttentionModal(false)}
        onConfirm={openReasonForLockModal}
        text="Once you have locked this protocol, please note that it cannot be unlocked."
        title="Continue Action"
        visible={attentionModal}
      />
      <ReasonForLockModal onClose={closeModals} onSuccess={closeModals} visible={reasonForLockModalOpen} />
      <Wrapper>
        <HeaderWrapper>
          <Header>{header}</Header>
          <SubHeader>{subHeader}</SubHeader>
        </HeaderWrapper>
        {withButton && buttonArea()}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.xsmall};
`;

const Header = styled.div`
  font-size: ${sizing.large};
  font-weight: ${fontWeights.bold};
  color: ${colors.black.primary};
`;

const SubHeader = styled.div`
  font-size: ${sizing.medium};
  font-weight: ${fontWeights.normal};
  color: ${colors.grey.quartenary};
`;

const ButtonWrapper = styled.div`
  height: 100%;
`;

const IconContainer = styled.div`
  position: relative;
`;

ProtocolHeader.propTypes = {
  header: PropTypes.string,
  loading: PropTypes.bool,
  subHeader: PropTypes.string,
  withButton: PropTypes.bool,
};

export default ProtocolHeader;
